import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import { useGamificationService } from '../services/gamificationService';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import type { MorningBriefing as MorningBriefingData } from '../types';

const BRIEFING_SHOWN_KEY = 'opspilot.briefing.lastShown';

function shouldShowBriefing(): boolean {
  const last = localStorage.getItem(BRIEFING_SHOWN_KEY);
  if (!last) return true;
  const today = new Date().toDateString();
  return last !== today;
}

function markBriefingShown(): void {
  localStorage.setItem(BRIEFING_SHOWN_KEY, new Date().toDateString());
}

const MorningBriefing: React.FC = () => {
  const service = useGamificationService();
  const [open, setOpen] = useState(false);
  const [briefing, setBriefing] = useState<MorningBriefingData | null>(null);
  const [loading, setLoading] = useState(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (!shouldShowBriefing()) return;
    let cancelled = false;
    setLoading(true);
    service.getMorningBriefing()
      .then((data) => {
        if (cancelled) return;
        setBriefing(data);
        setOpen(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        showSnackbar(err instanceof Error ? err.message : 'Morning briefing unavailable', 'warning');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [service, showSnackbar]);

  const handleClose = () => {
    markBriefingShown();
    setOpen(false);
  };

  if (loading || !briefing || !briefing.narrative) {
    return <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />;
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ShieldIcon color="primary" />
            <Typography variant="h6">Morning Briefing</Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}
          >
            Fortress health: {briefing.fortress_health}%
          </Typography>
        </Box>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {briefing.narrative}
          </Typography>

          {(briefing.critical_tasks?.length ?? 0) > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Needs Attention
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {briefing.critical_tasks.map((task) => (
                  <Chip
                    key={task.task_id}
                    label={`${task.title} · ${task.quest_title}`}
                    size="small"
                    variant="outlined"
                    color="warning"
                  />
                ))}
              </Box>
            </Box>
          )}

          {(briefing.quick_wins?.length ?? 0) > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Quick Wins
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {briefing.quick_wins.map((task) => (
                  <Chip
                    key={task.task_id}
                    label={task.title}
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Begin
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </>
  );
};

export default MorningBriefing;
